/**
 * NeedsYouList — the top Inbox lane (Inbox V2, spec §4.1/§4.3).
 *
 * Flat, newest-first list of OPEN items that require active human action (asks,
 * approvals, blockers, for-approval gates). Each row is a POINTER, not the
 * payload: source breadcrumb (Org · Project · #Card) + intent glyph + title +
 * a one-line body_preview + the viewer-local time + a [→] deep-link.
 *
 * Critical behaviour (§4.3): NEVER auto-mark-seen on render — seeing an ask is
 * not answering it. We do not flip seen_at here; that happens when the AskPopup
 * opens (later phase). Row click only navigates (deep-links to the card; for an
 * ask, with ?ask=<askId> so the popup target — built in 3b-2 — auto-opens).
 *
 * ADR-057: monochrome; the only colour is the tiny intent glyph via a semantic
 * token. English only.
 */
import { ArrowRight, Loader2 } from 'lucide-react';
import * as React from 'react';

import { env } from '../../env';
import type { Mention } from '../../lib/api/mentions';
import { cn } from '../../lib/utils';
import { formatLocalTime } from '../../lib/time-group';
import { AccessRequestPopup } from '../session/access-request-popup';
import { useToaster } from '../ui/toast';
import { intentDisplay, resolveInboxLink, useCardScope, useInboxNavigate } from './inbox-shared';
import { OrgBadge } from './org-badge';

interface NeedsYouListProps {
  items: Mention[];
  isLoading: boolean;
  /** Called after a row click navigates (e.g. to close a popover/sheet). */
  onNavigate?: () => void;
}

export function NeedsYouList({ items, isLoading, onNavigate }: NeedsYouListProps) {
  // A card-less access-request row opens the AccessRequestPopup as a standalone
  // modal (it renders entirely from getAccessRequest(requestId); no card is
  // required). Lifted here so ONE popup instance is shared by all rows.
  const [accessRequest, setAccessRequest] = React.useState<{
    requestId: string;
    cardId: string | null;
  } | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground md:px-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p className="px-4 py-6 text-[13px] text-muted-foreground md:px-6">
        Nothing needs you right now.
      </p>
    );
  }
  return (
    <>
      {/* I4: under the light system rows are space-separated (two-line, 64px) —
          drop the per-row divider so the lane is one quiet stack, not a ladder.
          OFF keeps the divide-y (byte-identical). */}
      <ul
        className={cn(!env.VITE_DESIGN_LIGHT && 'divide-y divide-border')}
        data-testid="needs-you-list"
      >
        {items.map((m) => (
          <NeedsYouRow
            key={m.id}
            mention={m}
            onNavigate={onNavigate}
            onOpenAccessRequest={(requestId, cardId) => setAccessRequest({ requestId, cardId })}
          />
        ))}
      </ul>
      {/* Standalone access-request popup — opened by a card-less access_request
          row (a dead click before this). Card-backed rows still navigate to the
          card, whose route auto-opens the same popup in session context. */}
      {accessRequest ? (
        <AccessRequestPopup
          requestId={accessRequest.requestId}
          cardId={accessRequest.cardId}
          open
          onClose={() => setAccessRequest(null)}
        />
      ) : null}
    </>
  );
}

interface NeedsYouRowProps {
  mention: Mention;
  onNavigate?: () => void;
  /** Open the standalone AccessRequestPopup (card-less access_request rows). */
  onOpenAccessRequest: (requestId: string, cardId: string | null) => void;
}

function NeedsYouRow({ mention, onNavigate, onOpenAccessRequest }: NeedsYouRowProps) {
  const inboxNavigate = useInboxNavigate();
  const { toast } = useToaster();
  // Pass the mention's org so the breadcrumb resolves the CORRECT org (the Inbox
  // is cross-org; the row's org is the item's org, not the active one).
  const scope = useCardScope(mention.cardId, mention.orgId);
  const { Icon, colorClass, label } = intentDisplay(mention.intent);
  const isUnread = mention.seenAt === null;

  // Deep-link only. NO mark-seen here (spec §4.3). The card route / AskPopup own
  // the seen/acted lifecycle. The card may be in a NON-active org, so switch org
  // context first (the nav helper does it) or the board opens the wrong org.
  const handleClick = async (): Promise<void> => {
    const target = await resolveInboxLink(mention);

    // Access requests do NOT depend on a card — the popup renders entirely from
    // getAccessRequest(source_id). When the row resolves to a card, navigate
    // there (the card route auto-opens the popup, keeping session context);
    // otherwise open the popup standalone so a card-less request is still
    // actionable (it was a dead click before). Keep the surrounding popover open
    // in the standalone case — closing it (onNavigate) would unmount the popup.
    if (mention.intent === 'access_request') {
      if (target) {
        onNavigate?.();
        await inboxNavigate(target, mention.orgId);
      } else {
        onOpenAccessRequest(mention.sourceId, mention.cardId ?? null);
      }
      return;
    }

    onNavigate?.();
    if (!target) {
      // The row's target card is gone (deleted / re-homed) — surface feedback
      // instead of a silent no-op.
      toast({
        title: 'Unavailable',
        description: "This item's card is no longer available.",
        variant: 'destructive',
      });
      return;
    }
    await inboxNavigate(target, mention.orgId);
  };

  // I2 (light): the OrgBadge chip is dropped in favour of a plain first crumb —
  // the org name leads the muted breadcrumb (`Org · Project · #Card`), no chip,
  // no building icon. OFF keeps the badge, so the crumb stays project · #card to
  // avoid duplicating the org name the badge already shows.
  const crumbs = (
    env.VITE_DESIGN_LIGHT
      ? [scope.orgName, scope.projectName, scope.cardTitle ? `#${scope.cardTitle}` : null]
      : [scope.projectName, scope.cardTitle ? `#${scope.cardTitle}` : null]
  )
    .filter(Boolean)
    .join(' · ');

  return (
    <li>
      <button
        type="button"
        onClick={() => void handleClick()}
        data-testid="needs-you-row"
        data-unread={isUnread}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-foreground/5 active:bg-foreground/10 md:px-6',
          'min-h-[64px]',
          isUnread && 'bg-foreground/[0.03]',
        )}
      >
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', colorClass)} aria-label={label} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            {/* Org — a bordered chip today; a plain first crumb under the light
                system (I2, rendered inline in `crumbs` above). */}
            {env.VITE_DESIGN_LIGHT ? null : <OrgBadge orgId={mention.orgId} />}
            {crumbs ? (
              <span className="truncate text-[11px] text-muted-foreground">{crumbs}</span>
            ) : (
              <span className="text-[11px] text-muted-foreground">{label}</span>
            )}
            <time
              dateTime={mention.createdAt}
              className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground"
            >
              {formatLocalTime(mention.createdAt)}
            </time>
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium text-foreground">
            {mention.title ?? label}
          </span>
          {mention.bodyPreview ? (
            <span className="mt-0.5 block truncate text-[12px] leading-snug text-muted-foreground">
              {mention.bodyPreview}
            </span>
          ) : null}
        </span>
        {/* I3: the whole row is the affordance — under the light system the
            per-row trailing arrow is decorative chrome ×N, so drop it. OFF keeps
            it (byte-identical). */}
        {env.VITE_DESIGN_LIGHT ? null : (
          <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
      </button>
    </li>
  );
}
